import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { cart, updateQuantity, removeFromCart, loading } = useCart();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paypalClientId, setPaypalClientId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch PayPal client ID
    const fetchPayPalClientId = async () => {
      try {
        const response = await axios.get(`${API}/paypal/client-id`);
        setPaypalClientId(response.data.client_id);
      } catch (error) {
        console.error('Error fetching PayPal client ID:', error);
      }
    };
    fetchPayPalClientId();
  }, []);

  const handleQuantityChange = async (productId, newQuantity) => {
    try {
      await updateQuantity(productId, newQuantity);
    } catch (error) {
      toast.error(t('errorAddingToCart'));
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success(t('removedFromCart'));
    } catch (error) {
      toast.error(t('errorAddingToCart'));
    }
  };

  const handleStripeCheckout = async () => {
    if (!isAuthenticated) {
      toast.error(t('loginRequired'));
      navigate('/login');
      return;
    }

    if (cart.items.length === 0) {
      toast.error(t('cartEmpty'));
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(
        `${API}/checkout/create-session`,
        { origin_url: window.location.origin },
        { headers: getAuthHeader() }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || t('errorCheckout'));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const createPayPalOrder = async () => {
    setCheckoutLoading(true);
    try {
      const response = await axios.post(
        `${API}/paypal/create-order`,
        { origin_url: window.location.origin },
        { headers: getAuthHeader() }
      );
      // Redirect to PayPal
      window.location.href = response.data.url;
    } catch (error) {
      console.error('PayPal error:', error);
      toast.error(error.response?.data?.detail || t('errorCheckout'));
      setCheckoutLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 py-16" data-testid="cart-page">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <ShoppingBag className="h-16 w-16 text-mrc-navy mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-mrc-navy mb-4">
            {t('yourCart')}
          </h1>
          <p className="text-muted-foreground mb-8">
            {t('loginToCheckout')}
          </p>
          <Link to="/login">
            <Button className="bg-mrc-navy hover:bg-mrc-navy/90 text-white px-8 py-6">
              {t('login')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white p-6 flex gap-6">
                <div className="bg-gray-200 w-24 h-24" />
                <div className="flex-1 space-y-2">
                  <div className="bg-gray-200 h-6 w-48" />
                  <div className="bg-gray-200 h-4 w-24" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="cart-page">
      {/* Header */}
      <div className="bg-mrc-navy py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl font-bold text-white">
            {t('yourCart')}
          </h1>
          <p className="text-gray-300 mt-2">
            {cart.items.length} {t('itemsInCart')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {cart.items.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold text-mrc-navy mb-4">
              {t('cartEmpty')}
            </h2>
            <p className="text-muted-foreground mb-8">
              {t('noProductsYet')}
            </p>
            <Link to="/products">
              <Button className="bg-mrc-navy hover:bg-mrc-navy/90 text-white px-8">
                {t('continueShopping')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item) => (
                <div 
                  key={item.product_id} 
                  className="bg-white p-4 md:p-6 flex gap-4 md:gap-6"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <Link to={`/products/${item.product_id}`} className="shrink-0">
                    <img 
                      src={item.product?.image_url} 
                      alt={item.product?.name}
                      className="w-20 h-20 md:w-24 md:h-24 object-contain bg-gray-50"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link 
                      to={`/products/${item.product_id}`}
                      className="font-heading text-lg font-semibold text-mrc-navy hover:underline line-clamp-1"
                    >
                      {item.product?.name}
                    </Link>
                    <p className="text-sm text-muted-foreground">{item.product?.category}</p>
                    <p className="price text-lg font-bold text-mrc-navy mt-1">
                      ${item.product?.price.toFixed(2)}
                    </p>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center border">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          data-testid={`decrease-${item.product_id}`}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="px-3 py-1 font-mono text-sm">
                          {item.quantity}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                          data-testid={`increase-${item.product_id}`}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(item.product_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="price text-lg font-bold text-mrc-navy">
                      ${item.subtotal?.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 sticky top-24">
                <h2 className="font-heading text-xl font-bold text-mrc-navy mb-6">
                  {t('orderSummary')}
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t('subtotal')}</span>
                    <span className="price">${cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>{t('shipping')}</span>
                    <span className="text-green-600">{t('free')}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between font-bold text-lg">
                      <span>{t('total')}</span>
                      <span className="price text-mrc-navy">${cart.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="mb-6">
                  <p className="text-sm font-medium mb-3">Chwazi metòd peman:</p>
                  <div className="space-y-2">
                    <label className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${paymentMethod === 'stripe' ? 'border-mrc-navy bg-mrc-navy/5' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        name="payment"
                        value="stripe"
                        checked={paymentMethod === 'stripe'}
                        onChange={() => setPaymentMethod('stripe')}
                        data-testid="payment-method-stripe"
                        className="text-mrc-navy"
                      />
                      <CreditCard className="h-5 w-5 text-mrc-navy" />
                      <span className="font-medium">Kat Kredi/Debi</span>
                    </label>
                    <label className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${paymentMethod === 'paypal' ? 'border-mrc-navy bg-mrc-navy/5' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        name="payment"
                        value="paypal"
                        checked={paymentMethod === 'paypal'}
                        onChange={() => setPaymentMethod('paypal')}
                        data-testid="payment-method-paypal"
                        className="text-mrc-navy"
                      />
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#003087">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.644h6.603c2.156 0 3.737.54 4.704 1.605.932 1.027 1.217 2.455.847 4.244-.018.09-.04.18-.063.27a6.63 6.63 0 0 1-.075.303c-.806 3.024-3.055 4.49-6.694 4.49H8.928a.77.77 0 0 0-.757.644l-.95 5.96a.641.641 0 0 1-.633.54l-.512.005z"/>
                        <path d="M19.623 7.217c-.006.046-.014.091-.02.137-.801 4.115-3.55 5.54-7.064 5.54H10.88a.871.871 0 0 0-.858.731l-.89 5.646-.252 1.6a.457.457 0 0 0 .452.53h3.176c.371 0 .687-.27.745-.636l.031-.163.59-3.735.038-.206a.756.756 0 0 1 .747-.64h.47c3.046 0 5.431-1.237 6.129-4.815.292-1.494.14-2.742-.632-3.62a2.992 2.992 0 0 0-.853-.669 7.85 7.85 0 0 1-.15 1.3z" fill="#003087"/>
                      </svg>
                      <span className="font-medium">PayPal</span>
                    </label>
                  </div>
                </div>

                {/* Payment Buttons */}
                {paymentMethod === 'stripe' ? (
                  <Button
                    onClick={handleStripeCheckout}
                    disabled={checkoutLoading || cart.items.length === 0}
                    className="w-full bg-mrc-orange hover:bg-mrc-orange/90 text-white py-6 text-lg font-semibold uppercase tracking-wider"
                    data-testid="checkout-btn"
                  >
                    {checkoutLoading ? t('processing') : t('payNow')}
                  </Button>
                ) : (
                  <Button
                    onClick={createPayPalOrder}
                    disabled={checkoutLoading || cart.items.length === 0}
                    className="w-full bg-[#0070ba] hover:bg-[#003087] text-white py-6 text-lg font-semibold"
                    data-testid="paypal-btn"
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="white">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.644h6.603c2.156 0 3.737.54 4.704 1.605.932 1.027 1.217 2.455.847 4.244-.018.09-.04.18-.063.27a6.63 6.63 0 0 1-.075.303c-.806 3.024-3.055 4.49-6.694 4.49H8.928a.77.77 0 0 0-.757.644l-.95 5.96a.641.641 0 0 1-.633.54l-.512.005z"/>
                    </svg>
                    {checkoutLoading ? t('processing') : 'Peye ak PayPal'}
                  </Button>
                )}

                <Link to="/products" className="block text-center mt-4">
                  <Button variant="outline" className="w-full">
                    {t('continueShopping')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
